import { personName } from "@/lib/issues";
import type { ProjectDetail, ProjectIssue, ProjectUser } from "@/types/api";

/** What the picker needs to know about the issue. */
export type PickerIssue = Pick<
  ProjectIssue,
  "id" | "created_by" | "assigned_to" | "contributors"
>;

/**
 * The project's members who aren't on the issue, by name. Left out: the raiser
 * and the assignee (on it already; the server refuses them), everyone in
 * Contributors, the viewer, and the owner, who holds no membership and already
 * sees and does everything. See [[contributor-picker.spec]].
 */
export function pickableMembers(
  project: Pick<ProjectDetail, "owner" | "members">,
  issue: PickerIssue,
  currentUserId: string | undefined,
): ProjectUser[] {
  const onIssue = new Set<string>(
    [
      issue.created_by?.id,
      issue.assigned_to?.id,
      currentUserId,
      project.owner.id,
      ...(issue.contributors ?? []).map((row) => row.user.id),
    ].filter((id): id is string => Boolean(id)),
  );

  const seen = new Set<string>();
  const people: ProjectUser[] = [];
  for (const { user } of project.members) {
    if (onIssue.has(user.id) || seen.has(user.id)) continue;
    seen.add(user.id);
    people.push(user);
  }
  return people.sort((a, b) => personName(a).localeCompare(personName(b)));
}
